/**
 * core · Inbox e preferências de notificação (telas 2.1 e 2.3).
 *
 * O catálogo de categorias é fechado e compartilhado pelos dois lados do fio. A
 * ordem é a do seed §14.18; não ordenar alfabeticamente é deliberado, porque a
 * matriz do perfil e o filtro da Inbox precisam permanecer iguais.
 */
import { z } from 'zod'
import {
  alcanca,
  temMarcador,
  veValoresFinanceiros,
  type Acao,
  type Marcador,
  type Modulo,
  type NivelDeAcesso,
} from '@dv/domain'
import { dataNominal, lista, uuid } from './comum.js'
import { referenciaDeUsuario } from './usuarios.js'

export const CHAVES_DE_CATEGORIA_DE_NOTIFICACAO = [
  'tarefas_prazos',
  'negocios',
  'comentarios_mencoes',
  'recebiveis_cobranca',
  'faturamento_nfs',
  'comissoes',
  'aprovacoes',
  'aniversarios',
  'rh',
  'orcamento_dre',
  'campanhas',
  'sistema_seguranca',
] as const

export const chaveDeCategoriaDeNotificacao = z.enum(CHAVES_DE_CATEGORIA_DE_NOTIFICACAO)
export type ChaveDeCategoriaDeNotificacao = z.infer<typeof chaveDeCategoriaDeNotificacao>

export const canalDeNotificacao = z.enum(['sistema', 'email', 'whatsapp'])
export type CanalDeNotificacao = z.infer<typeof canalDeNotificacao>

export const categoriaDeNotificacao = z.object({
  chave: chaveDeCategoriaDeNotificacao,
  nome: z.string(),
  ordem: z.number().int(),
})
export type CategoriaDeNotificacao = z.infer<typeof categoriaDeNotificacao>

/**
 * Quem ORIGINOU a notificacao, quando foi uma pessoa. Nulo e o caso comum: o que
 * nasce de rotina automatica nao tem autor, e a 2.1 mostra entao o icone da
 * categoria no lugar do avatar de iniciais. A coluna existe desde a 0048
 * (notificacao.created_by); o que faltava era ela subir pelo fio.
 */
export const autorDeNotificacao = referenciaDeUsuario
export type AutorDeNotificacao = z.infer<typeof autorDeNotificacao>

export const notificacao = z.object({
  id: uuid,
  categoria: categoriaDeNotificacao,
  titulo: z.string(),
  corpo: z.string(),
  entidadeTipo: z.string().nullable(),
  entidadeId: uuid.nullable(),
  lidaEm: z.string().nullable(),
  arquivadaEm: z.string().nullable(),
  criadaEm: z.string(),
  autor: autorDeNotificacao.nullable(),
})
export type Notificacao = z.infer<typeof notificacao>
const listaPaginadaDeNotificacoes = lista(notificacao)
export const listaDeNotificacoes = listaPaginadaDeNotificacoes.extend({
  meta: listaPaginadaDeNotificacoes.shape.meta.extend({ naoLidas: z.number().int().min(0) }),
})
export type ListaDeNotificacoes = z.infer<typeof listaDeNotificacoes>

export const filtrosDeNotificacoes = z.object({
  lidas: z.boolean().optional(),
  categoria: chaveDeCategoriaDeNotificacao.optional(),
  arquivadas: z.boolean().default(false),
  periodoDe: dataNominal.optional(),
  periodoAte: dataNominal.optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(200).default(50),
})
export type FiltrosDeNotificacoes = z.infer<typeof filtrosDeNotificacoes>

export const badgeDeNotificacoes = z.object({ naoLidas: z.number().int().min(0) })
export type BadgeDeNotificacoes = z.infer<typeof badgeDeNotificacoes>

export const preferenciaDeNotificacao = z.object({
  categoria: categoriaDeNotificacao,
  canal: canalDeNotificacao,
  habilitada: z.boolean(),
})
export type PreferenciaDeNotificacao = z.infer<typeof preferenciaDeNotificacao>

export const preferenciasDeNotificacao = z.object({ data: z.array(preferenciaDeNotificacao) })
export type PreferenciasDeNotificacao = z.infer<typeof preferenciasDeNotificacao>

export const entradaDePreferenciaDeNotificacao = z.object({
  categoria: chaveDeCategoriaDeNotificacao,
  canal: canalDeNotificacao,
  habilitada: z.boolean(),
})
export type EntradaDePreferenciaDeNotificacao = z.infer<typeof entradaDePreferenciaDeNotificacao>

// ============================================================ a política (DS-637)

/**
 * Como cada evento usa o e-mail: `obrigatorio` vai para todos do público e a célula não se
 * desliga; `preferencia` segue a matriz pessoal; `nunca` fica só na Inbox.
 */
export const MODOS_DO_EMAIL = ['obrigatorio', 'preferencia', 'nunca'] as const
export type ModoDoEmail = (typeof MODOS_DO_EMAIL)[number]

/** Uma exigência de nível: as mesmas perguntas do guard (`@dv/domain`). */
export interface ExigenciaDeNivel {
  readonly modulo: Modulo
  /** A ação mínima; ausente = `ver`. */
  readonly acao?: Acao
  readonly marcador?: Marcador
  readonly valores?: true
}

export interface DefinicaoDeEvento {
  readonly categoria: ChaveDeCategoriaDeNotificacao
  readonly inbox: boolean
  readonly email: ModoDoEmail
  /** `resumo`: o e-mail sai no resumo diário, nunca como aviso avulso. */
  readonly agregacao: 'unitario' | 'resumo'
  /**
   * Quem o evento pode alcançar, pelo nível: alguma destas exigências, por permissão ou por
   * responsabilidade (a permissão da rota que faz de alguém o solicitante). `todos`: qualquer
   * pessoa interna (a menção). É o que decide a matriz POR NÍVEL (E-w).
   */
  readonly alcanca: readonly ExigenciaDeNivel[] | 'todos'
}

const APROVADOR: readonly ExigenciaDeNivel[] = [{ modulo: 'aprovacao_desembolso', marcador: 'aprovar', valores: true }]
/** Quem registra o que vai a aprovação: a permissão do `@Exige` de cada rota produtora. */
const SOLICITANTE: readonly ExigenciaDeNivel[] = [
  { modulo: 'contas_a_pagar', acao: 'criar' },
  { modulo: 'comissoes', acao: 'editar_todos' },
  { modulo: 'rh', acao: 'editar_todos' },
]
const TESOURARIA: readonly ExigenciaDeNivel[] = [{ modulo: 'tesouraria', acao: 'editar_todos', valores: true }]

/**
 * O CATÁLOGO de eventos, em código (D-187): sem tabela, sem motor. A SPA lê a projeção dele
 * (o estado de cada célula), e a API, o comportamento; uma fonte só, para a tela e o servidor
 * nunca discordarem sobre o que um interruptor faz.
 */
export const EVENTOS_DE_NOTIFICACAO = {
  /** N1 · entra em aprovação: quem pode decidir, por e-mail obrigatório. */
  aprovacao_pendente: { categoria: 'aprovacoes', inbox: true, email: 'obrigatorio', agregacao: 'unitario', alcanca: APROVADOR },
  /** N2 · falta a 2ª aprovação. */
  segunda_aprovacao: { categoria: 'aprovacoes', inbox: true, email: 'obrigatorio', agregacao: 'unitario', alcanca: APROVADOR },
  /** N3 · a rejeição chega a quem registrou, com motivo e observação. */
  aprovacao_rejeitada: { categoria: 'aprovacoes', inbox: true, email: 'obrigatorio', agregacao: 'unitario', alcanca: SOLICITANTE },
  /** N3 · aprovação concluída: só a Inbox. */
  aprovacao_concluida: { categoria: 'aprovacoes', inbox: true, email: 'nunca', agregacao: 'unitario', alcanca: SOLICITANTE },
  /** N3 · a 1ª de duas aprovações: só a Inbox. */
  primeira_aprovacao: { categoria: 'aprovacoes', inbox: true, email: 'nunca', agregacao: 'unitario', alcanca: SOLICITANTE },
  /** A seção obrigatória "Aguardando a sua decisão" do resumo diário. */
  aprovacoes_aguardando: { categoria: 'aprovacoes', inbox: false, email: 'obrigatorio', agregacao: 'resumo', alcanca: APROVADOR },
  /**
   * N4 · negócio ganho: o responsável e quem vê a carteira inteira. Alcança QUALQUER nível (decisão 4.138, 10/10):
   * qualquer pessoa ativa pode ser a responsável de um negócio, e a responsabilidade direta pelo objeto basta para
   * este evento, com ou sem o módulo Negócios. Não concede acesso: sem o módulo, o aviso sai sem link (o botão leva à
   * Inbox) e em texto mínimo; sem valores, sem o valor. Por isso Negócios × Sistema e × E-mail nunca ficam
   * indisponíveis "pelo nível".
   */
  negocio_ganho: { categoria: 'negocios', inbox: true, email: 'preferencia', agregacao: 'unitario', alcanca: 'todos' },
  /** N5 · a parcela chega ao dia de faturar: quem pode registrar a NF, no resumo das 07:30. */
  parcela_no_dia_de_faturar: {
    categoria: 'faturamento_nfs', inbox: true, email: 'preferencia', agregacao: 'resumo',
    alcanca: [{ modulo: 'faturamento', acao: 'criar', valores: true }],
  },
  /** N6 · Sicoob grave: certificado vencido, a 15 dias, recusado, ou acesso recusado. */
  sicoob_grave: { categoria: 'sistema_seguranca', inbox: true, email: 'obrigatorio', agregacao: 'unitario', alcanca: TESOURARIA },
  /** N6 · os outros avisos do Sicoob: só a Inbox, como antes. */
  sicoob_aviso: { categoria: 'sistema_seguranca', inbox: true, email: 'nunca', agregacao: 'unitario', alcanca: TESOURARIA },
  /** I-1/I-2 · menções: sem mudança. */
  mencao: { categoria: 'comentarios_mencoes', inbox: true, email: 'nunca', agregacao: 'unitario', alcanca: 'todos' },
  /** I-3 · orçamento submetido: quem aprova o orçamento. */
  orcamento_submetido: {
    categoria: 'orcamento_dre', inbox: true, email: 'nunca', agregacao: 'unitario',
    alcanca: [{ modulo: 'orcamento', marcador: 'aprovar' }],
  },
  /** I-4 · orçamento decidido: o autor da versão. */
  orcamento_decidido: {
    categoria: 'orcamento_dre', inbox: true, email: 'nunca', agregacao: 'unitario',
    alcanca: [{ modulo: 'orcamento', acao: 'criar' }],
  },
  /** I-7 · ciclos de assinatura no teto: agora respeitando a preferência. */
  ciclos_truncados: {
    categoria: 'recebiveis_cobranca', inbox: true, email: 'nunca', agregacao: 'unitario',
    alcanca: [{ modulo: 'recebiveis' }],
  },
  /**
   * D-191 (emenda à D-187), P-42 · os alertas de COLETA do DV360 (vínculo, cobertura e sincronização: não encontrada,
   * suspensa, falhas seguidas, cota, relatório atrasado, revisão atrasada, histórico limitado, moeda divergente, borda
   * quente, Campaign ativa com a campanha encerrada, sincronização encerrada no teto). Público: os responsáveis da
   * campanha e quem tem `midia · editar_todos`, que abrem a ficha; uma vez por episódio; texto sem valores. Só a Inbox
   * (DS-672): nenhum e-mail obrigatório fora dos da D-187, e uma sincronização de 20 Campaigns não vira rajada de e-mail.
   */
  dv360_coleta: {
    categoria: 'campanhas', inbox: true, email: 'nunca', agregacao: 'unitario',
    alcanca: [{ modulo: 'midia' }],
  },
  /**
   * D-191, P-42 · os alertas de GASTO do DV360 (o Media Cost antes ou depois do período planejado, durante a pausa,
   * depois da conclusão ou do cancelamento, o estorno): o mesmo público, só quem vê custos e margem
   * (`midia_custos_margem · ver`, conferido por destinatário); texto sem valores; só a Inbox.
   */
  dv360_gasto: {
    categoria: 'campanhas', inbox: true, email: 'nunca', agregacao: 'unitario',
    alcanca: [{ modulo: 'midia' }],
  },
} as const satisfies Record<string, DefinicaoDeEvento>

export type EventoDeNotificacao = keyof typeof EVENTOS_DE_NOTIFICACAO

/** O nível atende a exigência? As mesmas funções do guard. */
export function atendeExigencia(nivel: NivelDeAcesso, exige: ExigenciaDeNivel): boolean {
  if (!alcanca(nivel, exige.modulo, exige.acao ?? 'ver')) return false
  if (exige.marcador !== undefined && !temMarcador(nivel, exige.modulo, exige.marcador)) return false
  if (exige.valores === true && !veValoresFinanceiros(nivel, exige.modulo)) return false
  return true
}

/** O evento alcança alguém deste nível? */
export function eventoAlcancaONivel(evento: EventoDeNotificacao, nivel: NivelDeAcesso): boolean {
  const def: DefinicaoDeEvento = EVENTOS_DE_NOTIFICACAO[evento]
  return def.alcanca === 'todos' || def.alcanca.some((e) => atendeExigencia(nivel, e))
}

export type EstadoDaCelula = 'livre' | 'obrigatoria' | 'indisponivel'

/**
 * Por que a célula está indisponível, para a tela dizer com as palavras certas:
 * `canal_inativo` (o WhatsApp), `sem_avisos` (a categoria não gera aviso), `so_inbox` (o aviso
 * existe e chega só pela Inbox) e `nivel` (existe, mas não alcança o seu nível).
 */
export type RazaoDaIndisponibilidade = 'canal_inativo' | 'sem_avisos' | 'so_inbox' | 'nivel'

export interface CelulaDePreferencia {
  readonly estado: EstadoDaCelula
  readonly razao: RazaoDaIndisponibilidade | null
}

function eventosDa(categoria: ChaveDeCategoriaDeNotificacao): EventoDeNotificacao[] {
  return (Object.keys(EVENTOS_DE_NOTIFICACAO) as EventoDeNotificacao[])
    .filter((e) => EVENTOS_DE_NOTIFICACAO[e].categoria === categoria)
}

/**
 * A célula da matriz 2.3 (decisão 6.9, E-w): nenhum interruptor ativo fica sem efeito.
 *
 *  - WhatsApp: sempre indisponível (sem produtor).
 *  - Sistema: livre se algum evento da categoria grava a Inbox.
 *  - E-mail: livre se algum evento está em `preferencia`; obrigatória se algum está em
 *    `obrigatorio`; senão indisponível.
 *  - Com o nível: os eventos que não alcançam o nível saem da conta; se nenhum sobra, a célula
 *    fica indisponível com a razão `nivel`. GET e PUT das preferências usam esta mesma função.
 */
export function celulaDePreferencia(
  categoria: ChaveDeCategoriaDeNotificacao,
  canal: CanalDeNotificacao,
  nivel?: NivelDeAcesso,
): CelulaDePreferencia {
  if (canal === 'whatsapp') return { estado: 'indisponivel', razao: 'canal_inativo' }
  const todos = eventosDa(categoria)
  const doNivel = nivel === undefined ? todos : todos.filter((e) => eventoAlcancaONivel(e, nivel))
  if (canal === 'sistema') {
    const comInbox = (lista: EventoDeNotificacao[]) => lista.filter((e) => EVENTOS_DE_NOTIFICACAO[e].inbox)
    if (comInbox(todos).length === 0) return { estado: 'indisponivel', razao: 'sem_avisos' }
    if (comInbox(doNivel).length === 0) return { estado: 'indisponivel', razao: 'nivel' }
    return { estado: 'livre', razao: null }
  }
  const comEmail = (lista: EventoDeNotificacao[]) => lista.filter((e) => EVENTOS_DE_NOTIFICACAO[e].email !== 'nunca')
  if (comEmail(todos).length === 0) return { estado: 'indisponivel', razao: todos.length === 0 ? 'sem_avisos' : 'so_inbox' }
  const efetivos = comEmail(doNivel)
  if (efetivos.length === 0) return { estado: 'indisponivel', razao: 'nivel' }
  if (efetivos.some((e) => EVENTOS_DE_NOTIFICACAO[e].email === 'preferencia')) return { estado: 'livre', razao: null }
  return { estado: 'obrigatoria', razao: null }
}

/** Só o estado, para quem não precisa da razão. */
export function estadoDaCelula(
  categoria: ChaveDeCategoriaDeNotificacao,
  canal: CanalDeNotificacao,
  nivel?: NivelDeAcesso,
): EstadoDaCelula {
  return celulaDePreferencia(categoria, canal, nivel).estado
}

/** A fila do resumo diário: o worker agenda, a API consome (DS-639). */
export const FILA_RESUMO_DIARIO = 'dv-notificacoes-resumo-diario'
/** 07:30 de São Paulo, todo dia. */
export const AGENDA_DO_RESUMO_DIARIO = { cron: '30 7 * * *', fuso: 'America/Sao_Paulo' } as const

/** O teto de itens por seção do resumo e por aviso em lista (E-r). */
export const TETO_DE_ITENS_POR_SECAO = 14

/**
 * Corta a lista no teto, mantendo a ordem de quem chama (os mais urgentes primeiro), e diz
 * quantos sobraram para a linha "e mais N". Payload sem teto estouraria o contrato do e-mail
 * e sumiria calado (E-r).
 */
export function limitarSecao<T>(itens: readonly T[], teto: number = TETO_DE_ITENS_POR_SECAO): { itens: T[]; restantes: number } {
  return { itens: itens.slice(0, teto), restantes: Math.max(0, itens.length - teto) }
}
